<template>
    <v-container class="space-y-lg pb-xl md:space-y-2xl">
        <div class="mx-sm space-y-md sm:mx-0">
            <v-h3 tag="h1">
                {{ story.name }}
            </v-h3>

            <v-storyblok-component :is="story.content.active_campaigns[0]"
                v-if="hasActiveCampaigns"
                :story="story.content.active_campaigns"
                :blok="story.content.active_campaigns[0]"
                :options="{ isEndDateHidden: true }" />

            <sb-rich-text v-if="story.content.description"
                :document="story.content.description"
                class="prose-5xs text-[color:var(--font-color)]"
                :style="{
                    '--font-color': story.content.description_color?.color,
                }" />
        </div>

        <!-- Sub categories menu-->
        <div v-if="subCategories.length > 0"
            class="grid grid-cols-1 gap-xs px-sm sm:px-0 md:grid-cols-3
                md:gap-sm
                lg:grid-cols-4">
            <nuxt-link v-for="category in subCategories"
                :key="category.id"
                :to="formatSbUrl(category.full_slug)"
                :aria-label="category.name"
                class="group grid h-[70px] grid-cols-4 items-center overflow-hidden rounded
                    bg-gray-500/5 hover:bg-gray-500/10">
                <!-- Image defined just for menu. -->
                <nuxt-picture
                    v-if="category.content.menu_image?.filename
                        && category.content.menu_image?.filename.length > 0"
                    :src="category.content.menu_image.filename"
                    provider="storyblok"
                    loading="lazy"
                    height="60"
                    width="60"
                    fit="in"
                    class="mx-auto px-xs"
                    :alt="category.content.meta.title"
                    :aria-label="t('aria.navigate', { page: category.name })"
                    sizes="xs:60px sm:60px md:60px lg:60px xl:60px 2xl:60px"
                    :img-attrs="{
                        class:'mix-blend-multiply w-auto'
                    }" />

                <!-- Fallback image if menu image is not defined. -->
                <nuxt-picture
                    v-else-if="category.content.image?.filename && category.content.image?.filename.length > 0"
                    :src="category.content.image.filename"
                    provider="storyblok"
                    loading="lazy"
                    height="70"
                    fit="in"
                    class="col-span-1 w-full"
                    :alt="category.content.meta.title"
                    :aria-label="t('aria.navigate', { page: category.name })"
                    sizes="xs:106px sm:106px md:82px lg:82px xl:103px 2xl:124px"
                    :img-attrs="{
                        class:'object-cover h-[70px] w-full mix-blend-multiply'
                    }" />

                <!-- Text content of menu. -->
                <p class="col-span-3 flex h-full items-center
                    bg-[color:var(--background-color)] px-sm font-prenton-cond
                    text-base leading-tight text-[color:var(--text-color)] decoration-[color:var(--text-color)]
                    group-hover:underline xl:text-lg"
                    :style="{
                        '--background-color': buttonBackgroundColor,
                        '--text-color': buttonFontColor
                    }">
                    <span class="line-clamp-2">
                        {{ addHyphens(category.name) }}
                    </span>
                </p>
            </nuxt-link>
        </div>

        <v-elevate-product-list
            v-slot="{
                products,
                productCount,
                hasNextPage,
                loadMore,
                getAdForIndex,
                showCtaForIndex,
                activeFiltersCount,
                isLoading,
                clearFilter,
            }"
            :campaign-only="isCampaignOnly"
            load-ads
            class="space-y-lg px-sm sm:px-0">
            <div class="flex flex-col justify-between">
                <p class="text-sm tracking-xl text-gray-500 md:block">
                    {{ productCount }} {{ t('amount-products') }}
                </p>

                <div class="mt-sm flex gap-lg lg:pt-md">
                    <button type="button"
                        class="flex items-center space-x-xs hover:underline"
                        @click="isModalOpen = true">
                        <template v-if="activeFiltersCount > 0">
                            <v-category-filter-count :active-filters-count="activeFiltersCount" />
                        </template>

                        <span>{{ t('filter-title') }}</span>

                        <v-icon icon="filter"
                            class="text-lg"></v-icon>
                    </button>

                    <v-button v-if="activeFiltersCount > 0"
                        theme="gray"
                        size="sm"
                        @click="clearFilter">
                        {{ t('filter-clear') }}
                    </v-button>
                </div>

                <v-elevate-common-facets class="mt-xs" />

                <v-category-order class="mt-md" />
            </div>

            <div class="grid grid-cols-2 gap-x-xs gap-y-lg
                md:grid-cols-3 md:gap-x-sm md:gap-y-xl lg:grid-cols-4">
                <template v-if="isLoading && products.length === 0">
                    <v-category-product-skeleton
                        v-for="index in 8"
                        :key="index" />
                </template>

                <template v-for="(product, index) in products"
                    :key="product.key">
                    <v-category-cta v-if="ctaContent != null && isCtaVisible && showCtaForIndex(index)"
                        :content="ctaContent"
                        class="col-span-full" />

                    <!-- Ads -->
                    <sb-listing-ad v-if="getAdForIndex(index)"
                        :ad="getAdForIndex(index)" />

                    <v-elevate-product-card :product="product" />
                </template>
            </div>

            <div class="grid md:grid-cols-8">
                <div class="space-y-md md:col-span-2 md:col-start-4">
                    <client-only>
                        <v-button class="w-full"
                            :theme="hasNextPage ? 'black' : 'gray'"
                            :disabled="!hasNextPage"
                            :aria-label="hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded')"
                            @click="loadMore">
                            {{ hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded') }}
                        </v-button>
                    </client-only>

                    <v-progress-bar :total="productCount"
                        :current="products.length">
                        {{ $t('loader.show-more-prefix') }} {{ products.length }}
                        / {{ productCount }} {{ $t('loader.show-more-suffix') }}
                    </v-progress-bar>

                    <button class="w-full text-base underline"
                        type="button"
                        @click="scrollToTop">
                        <span class="">{{ $t('loader.back-to-start') }}</span>
                    </button>
                </div>
            </div>

            <v-category-filter-modal :is-open="isModalOpen"
                :results="productCount"
                @close="isModalOpen = false" />
        </v-elevate-product-list>

        <nuxt-picture v-if="story.content.image?.filename"
            :src="story.content.image.filename"
            :alt="story.content.image?.alt ?? story.content.title"
            provider="storyblok"
            loading="lazy"
            sizes="xs:100vw sm:640px md:768px lg:325px xl:410px 2xl:496px"
            class="block lg:hidden"
            :img-attrs="{
                class: 'w-full rounded object-cover aspect-[21/9] lg:aspect-square'
            }" />

        <div v-if="story.content.content"
            class="space-y-sm break-words text-gray-500">
            <div
                :class="{
                    'line-clamp-3 max-h-28 md:line-clamp-6 md:max-h-40 lg:max-h-none': !showAll,
                }">
                <sb-rich-text :document="story.content.content"
                    class="prose-5xs" />
            </div>

            <button v-if="!showAll"
                type="button"
                class="text-sm outline-none hover:underline focus:underline
                focus-visible:rounded-2xs focus-visible:outline-2
                focus-visible:outline-white focus-visible:ring-4 focus-visible:ring-blue-600
                lg:text-base lg:font-bold"
                @click="showAll = true">
                {{ t('read-more') }}
            </button>

            <button v-if="showAll"
                type="button"
                class="text-sm outline-none hover:underline
                focus:underline focus-visible:rounded-2xs focus-visible:outline-2
                focus-visible:outline-white focus-visible:ring-4 focus-visible:ring-blue-600
                lg:text-base lg:font-bold"
                @click="showAll = false">
                {{ t('show-less') }}
            </button>
        </div>
    </v-container>
</template>

<script setup lang="ts">
import type { ISbStoryData } from 'storyblok-js-client';
import type { ISbRichtext } from '@storyblok/js';
import type {
    BlokAsset,
    MetaFieldsPlugin,
    CmdProductListPlugin,
    BlokColorpicker,
    ListingAdComponent,
} from '~~/types';

/**
 *  There was a super weird nuxt hydration bug where `hasNextPage` would
    evaluate to true in some attributes passed to the button and to
    false in some other attributes. Ie the button would show the "load more"
    text but still be disabled and greyed out even though there should be
    more matches to load. This would happen randomly when navigating to
    the page from any external (non-nuxt) link to this page. Also, when
    this occurred the initial SSR render seemed to return an empty product
    list on initial page load (for reasons unknown)
 */

const properties = defineProps<{
    story: ISbStoryData<CategoryLander>
}>();

defineEmits<{
    (event: 'clear-gp-filter'): void
}>();

const { t } = useI18n();
const { format: formatSbUrl } = useSbUrl();
const { hyphenate: addHyphens } = useHyphenation();

interface CategoryLander {
    component: 'sb-category-lander'
    meta: MetaFieldsPlugin
    image: BlokAsset
    title: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_menu: string[]
    content: ISbRichtext
    products: CmdProductListPlugin
    // eslint-disable-next-line @typescript-eslint/naming-convention
    campaign_only: boolean
    description: ISbRichtext
    // eslint-disable-next-line @typescript-eslint/naming-convention
    short_description: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    description_color: BlokColorpicker
    // eslint-disable-next-line @typescript-eslint/naming-convention
    on_pages: string[]
    ads: ListingAdComponent[]
    // eslint-disable-next-line @typescript-eslint/naming-convention
    active_campaigns: ISbStoryData[]
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_text_color: BlokColorpicker
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_background_color: BlokColorpicker
}

const categoryLevel = computed(() => properties.story.full_slug.replace(/\/$/, '').split('/').length);

const ctaContent = useStoryblokStory('global/category-cta');

const categoryId = computed(() => properties.story.uuid);

const isCtaVisible = computed(() => (
    ctaContent.value?.content.on_pages?.length
    && ctaContent.value?.content.on_pages.includes(categoryId.value)
));

const isModalOpen = ref(false);

const isCampaignOnly = computed(() => properties.story.content.campaign_only);

const showAll = ref(false);

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const subCategoriesData = await useAsyncStoryblokStories(`subcategories-${properties.story.full_slug}`, {
    /* eslint-disable @typescript-eslint/naming-convention */
    starts_with: properties.story.full_slug,
    excluding_ids: `${properties.story.id}`,
    per_page: 50,
    /* eslint-disable @typescript-eslint/naming-convention */
});

const subCategories = subCategoriesData
    .filter((story) => (
        story.full_slug
            .replace(/\/$/, '')
            .split('/').length === categoryLevel.value + 1
    ))
    .sort((a, b) => a.position - b.position);

const hasActiveCampaigns = computed(() => properties.story.content.active_campaigns?.length > 0);

const buttonColors = useStoryblokStory('global/generic-colors');

const buttonBackgroundColor = computed<string | undefined>(() => (
    buttonColors?.value?.content.category_background_color?.color ?? '#FFF'
));

const buttonFontColor = computed<string | undefined>(() => (
    buttonColors.value?.content.category_text_color?.color ?? '#000'
));

useSeoMeta({
    title: properties.story.content.meta?.title || properties.story.content.title || properties.story.name,
    // eslint-disable-next-line max-len
    description: (properties.story.content.meta?.description || properties.story.content?.short_description) ?? undefined,
    ogTitle: properties.story.content.meta?.title || properties.story.content.title || properties.story.name,
    // eslint-disable-next-line max-len
    ogDescription: (properties.story.content.meta?.description || properties.story.content?.short_description) ?? undefined,
    ogImage: properties.story.content?.image?.filename ?? undefined,
});

useDefaultDynamicHead({ includeQueryInCanonicalUrl: false });
</script>

<i18n lang="json">
{
    "et": {
        "amount-products": "toodet",
        "filter-title": "Kõik filtrid",
        "read-more": "Loe rohkem",
        "show-less": "Näita vähem"
    },
    "fi": {
        "amount-products": "tuotetta",
        "filter-title": "Kaikki suodattimet",
        "read-more": "Lue lisää",
        "show-less": "Näytä vähemmän"
    }
}
</i18n>
